import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Pagination, Popconfirm, Table } from "antd";
import { MinusSquareOutlined, PlusSquareOutlined } from "@ant-design/icons";
import api from "./store/api";
import { useDebounce } from "use-debounce";
import Notifications from "../../../helpers/Notifications";
import LocalLoading from "../../../components/Loading/Local";
import CustomInput from "../../../components/CustomInput";
import CustomButton from "../../../components/CustomButton";
import AddIcon from "../../../components/Icons/add.svg";
import UpdateIcon from "../../../components/Icons/update.svg";
import DeleteIcon from "../../../components/Icons/delete.svg";
import RestoreIcon from "../../../components/Icons/restore.svg";
import AuditIcon from "../../../components/Icons/audit.svg";
import RePTMsChangeForm from "./change";
import ModalAudit from "../ModalAudit";
import dayjs from "dayjs";
import "./style.css"
import { deleteAction, 
         restoreAction,
         getTreeAction,
         getPagedAllAction,
         clearAction } from "./store/actions";

const RePTMs = ({isModify = false}) => {
    const [visible, setVisible] = useState(false);
    const [record, setRecord] = useState(null);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState(null);
    const [visibleAudit, setVisibleAudit] = useState(false)
    const [idRecordAudit, setIdRecordAudit] = useState(null)
    const [expandedRows, setExpandedRows] = useState([]);
    
    const { paged, tree, changed, isLoading } = useSelector(state => state.rePtmReducer)
    const pageSize = 10;
    const [searchValue] = useDebounce(search, 500);
    const { t } = useTranslation(["common", "valid", "action"]);
    const dispatch = useDispatch();

    useEffect(() => {
        onPaginationChange(1);
    }, [searchValue]);

    useEffect(() => {
        var expandedNodes = [...document.getElementsByClassName("anticon-minus-square")]
        expandedNodes.forEach(node => node.classList.add("expandedRow"));
    }, [expandedRows]);

    useEffect(() => {
        if (changed) {
            Notifications.successNotice(changed, "", 10);
            setVisible(false);
            onPaginationChange(page);
            dispatch(clearAction())
        }
    }, [changed]);

    function onPaginationChange(value) {
        setPage(value);
        const params = {
            name: searchValue?.trim(),
            pageSize: pageSize,
            page: value,
        };
        isModify ? dispatch(getPagedAllAction(params)) : dispatch(getTreeAction(params));
    }

    const onExpandHandler = (expanded, record) => {
        let rows = Object.assign([], expandedRows);

        const sameLevelIndex = rows.findIndex(el => record.level == el.level)
        if(sameLevelIndex >= 0){
            rows.splice(sameLevelIndex, rows.length);
        }

        if(expanded && (record.level == 1 || record.level > rows.at(-1).level)){
            rows.push({
                id: record.id,
                level: record.level
            });
        }
        setExpandedRows(rows);
    }

    const comColumns = [
        {
            title: t("common:level"),
            dataIndex: "level",
            key: "level",
            width: "10%"
        },
        {
            title: t("common:code"),
            dataIndex: "code",
            key: "code",
            width: "10%",
        },
        {
            title: t("common:name"),
            dataIndex: "name",
            key: "name",
        },
        {
            title: t("common:measure"),
            dataIndex: ["reMeasurementUnit", "name"],
            key: "reMeasurementUnit",
            width: "10%"
        },
    ];

    const manageColumns = [
        ...comColumns,
        {
            title       : t("common:dateCreate"),
            dataIndex   : "createDate",
            key         : "createDate",
            width       : "10%",
            render      : (date) => dayjs(date).format("DD.MM.YYYY"),
        },
        {
            dataIndex: "id",
            key: "id",
            width: 100,
            render: (id, record) => (
                <div className="changeIcon">
                    <img alt=""
                        src={UpdateIcon}
                        title={t("action:edit")}
                        onClick={() => {
                            setVisible(true);
                            setRecord(record);
                        }}
                    />
                    <Popconfirm
                        cancelText={t("no")}
                        okText={t("yes")}
                        placement="left"
                        title={
                            !record.isDeleted
                                ? t("valid:sureToDelete")
                                : t("valid:sureToRestore")
                        }
                        onCancel={(e) => {
                            e.stopPropagation();
                            return null;
                        }}
                        onConfirm={(e) => {
                            e.stopPropagation();
                            !record.isDeleted
                                ? dispatch(deleteAction({ id }))
                                : dispatch(restoreAction({ id }));
                        }}
                    >
                        <img alt=""
                            src={!record.isDeleted ? DeleteIcon : RestoreIcon}
                            title={
                                !record.isDeleted
                                    ? t("action:delete")
                                    : t("action:restore")
                            }
                        />
                    </Popconfirm>
                    <img alt=""
                        className="audit-icon-svg"
                        src={AuditIcon}
                        title={t('common:historyOfChange')}
                        onClick={() => {
                            setVisibleAudit(true)
                            setIdRecordAudit(id)
                        }}
                    />
                </div>
            )
        }
    ];

    const expandableSetting =
        !isModify ?
            {
                expandRowByClick: true,
                expandedRowKeys: expandedRows.map(r => r.id),
                onExpand: onExpandHandler,
                expandIcon: ({record, expanded}) => record?.children?.length === 0 ? null :
                    expanded ?
                        <MinusSquareOutlined/> :
                        <PlusSquareOutlined/>,
            } : {};

    return (
        <>
            <div className="header-admin-nsi">
                <CustomInput
                    maxLength={64}
                    placeholder={t("action:enterNameCode")}
                    onChange={(e) => setSearch(e.target.value)}
                />
                {isModify && (
                    <CustomButton
                        className="add-nsi"
                        type="green"
                        onClick={() => {
                            setRecord(null);
                            setVisible(true);
                        }}
                    >
                        <div>
                            <img alt="" src={AddIcon} />
                            <span>{t("action:add")}</span>
                        </div>
                    </CustomButton>
                )}
            </div>
            <Table
                columns={isModify ? manageColumns : comColumns}
                dataSource={isModify ? paged?.items : tree}
                rowKey={({id}) => id}
                loading={{ indicator: <LocalLoading />, spinning: isLoading }}
                pagination={false}
                expandable={expandableSetting}
                scroll={{ x: 1100, y: 600 }}
            />
            {(isModify && paged?.count > pageSize) && (
                <Pagination
                    showQuickJumper
                    current={page}
                    pageSize={pageSize}
                    showSizeChanger={false}
                    size="small"
                    total={paged?.count}
                    onChange={onPaginationChange}
                />
            )}
            {isModify && (
                <RePTMsChangeForm
                    record={record}
                    setRecord={setRecord}
                    setVisible={setVisible}
                    visible={visible}
                />
            )}
            <ModalAudit
                columns={comColumns}
                setIdRecordAudit={setIdRecordAudit}
                idRecordAudit={idRecordAudit}
                setVisible={setVisibleAudit}
                visible={visibleAudit}
                pageSize={pageSize}
                func={api.getPagedAudit}
            />
        </>
    );
};

export default RePTMs;
